import type { Platform, SubjectPlaylist } from "../../../shared/api";
import { PLATFORM_VK, playlistUrl } from "../../../shared/api";
import type { FormState, PlatformSettings } from "../types";
import { ACCESS_PUBLIC, ACCESS_LINK, PLAYLIST_NONE } from "../utils";
import { ActionIcon, Box, Checkbox, Group, Stack, Text, Tooltip } from "@mantine/core";
import ExternalLink from "lucide-react/dist/esm/icons/external-link";
import { InlineSelect } from "../../../shared/components/InlineSelect";

interface PlatformBlockProps {
  platform: Platform;
  platformName: string;
  isEnabled: boolean;
  channels: any;
  formState: FormState;
  matchingPlaylists: any[];
  /** The selected subject's playlist on this platform, if it has one. */
  subjectPlaylist?: SubjectPlaylist | null;
  onToggle: (enabled: boolean) => void;
  onPatch: (patch: Partial<PlatformSettings>) => void;
}

export function PlatformBlock({
  platform,
  platformName,
  isEnabled,
  channels,
  formState,
  matchingPlaylists,
  subjectPlaylist,
  onToggle,
  onPatch,
}: PlatformBlockProps) {
  const platformSettings = formState.platforms.find(p => p.platform === platform);
  const channelRef = platformSettings?.channel_ref || '';
  const playlistRef = platformSettings?.playlist_ref || '';
  const isVK = platform === PLATFORM_VK;
  const access = isVK ? ACCESS_PUBLIC : (platformSettings?.access || ACCESS_PUBLIC);
  const accessOptions = isVK
    ? [{ value: ACCESS_PUBLIC, label: 'Публичный' }]
    : [
        { value: ACCESS_PUBLIC, label: 'Публичный' },
        { value: ACCESS_LINK, label: 'По ссылке' },
      ];

  const channelOptions = (channels?.[platform] ?? []).map((ch: any) => ({
    value: ch.id,
    label: ch.description,
  }));
  const playlistOptions = matchingPlaylists.map((pls: any) => ({
    value: pls.playlist_id,
    label: pls.title,
  }));
  const openUrl = channelRef && playlistRef && playlistRef !== PLAYLIST_NONE ? playlistUrl(platform, channelRef, playlistRef) : '';
  const mismatch = isEnabled && !!subjectPlaylist && !!playlistRef
    && (playlistRef !== subjectPlaylist.playlist_id || channelRef !== subjectPlaylist.channel_id);

  return (
    <Box w="100%">
      <Box pb="sm" mb="xs" style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
        <Checkbox
          checked={isEnabled}
          onChange={e => onToggle(e.currentTarget.checked)}
          styles={{ body: { alignItems: "center" }, label: { lineHeight: 1 } }}
          label={<Text fw={700} fz="lg">{platformName}</Text>}
        />
      </Box>

      <Stack gap={0}>
        <InlineSelect
          label="Канал"
          labelWidth={120}
          value={channelRef || null}
          data={channelOptions}
          onChange={val => onPatch({ channel_ref: val ?? '', playlist_ref: '' })}
          searchable
          disabled={!isEnabled}
        />
        <Group gap={4} wrap="nowrap">
          <div style={{ flex: 1, minWidth: 0 }}>
            <InlineSelect
              label="Плейлист"
              labelWidth={120}
              value={playlistRef || null}
              data={playlistOptions}
              onChange={val => onPatch({ playlist_ref: val ?? '' })}
              searchable
              disabled={!isEnabled}
              pinned={{ value: PLAYLIST_NONE, label: 'Без плейлиста' }}
            />
          </div>
          {openUrl && (
            <Tooltip label="Открыть плейлист" withArrow openDelay={300}>
              <ActionIcon component="a" href={openUrl} target="_blank" rel="noopener noreferrer" variant="subtle" color="gray" aria-label="Открыть плейлист">
                <ExternalLink size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
        {mismatch && <Text fz="xs" c="orange" pl={12}>Плейлист может не соответствовать предмету</Text>}
        <InlineSelect
          label="Доступ"
          labelWidth={120}
          value={access}
          data={accessOptions}
          onChange={val => onPatch({ access: val ?? ACCESS_PUBLIC })}
          disabled={!isEnabled || isVK}
        />
      </Stack>
    </Box>
  );
}
